import Loader from '@/components/common/Loader';
import { Badge } from '@/components/ui-kit/badge';
import { loadTranslations } from '@/i18n/i18n';
import { useLanguageContext } from '@/i18n/language-context';
import { slugToCamelCase } from '@/i18n/utils/slug-to-camel-case';
import { tWithFallback } from '@/i18n/utils/t-with-fallback';
import { Eye } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ForbiddenCard } from '@/components/common/ForbiddenCard';
import { useMyOgnization } from '@/api-client/use-my-ognization';
import ComingSoon from '../components/generate/ComingSoon';
import DynamicSmartFlow from '../components/generate/dynamic-smart-flow/DynamicSmartFlow';
import NoScreens from '../components/generate/NoScreens';
import { useGetTemplateBySlug } from '../hooks/use-templates';

const TemplateGeneratePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preview = params.get('preview');
  const { t } = useTranslation();
  const { currentLanguage } = useLanguageContext();

  const { data: template, isLoading } = useGetTemplateBySlug(slug);
  const { data: myOrgs } = useMyOgnization();

  // Org-owned templates are usable only by members of the owning org. Global
  // templates (no organizationId) stay open to everyone, guests included —
  // that's what keeps the public generate funnel working.
  // Called before the early returns — hooks can't live below one.
  const templateOrgId = template?.organizationId;
  const canUseTemplate =
    !templateOrgId ||
    (myOrgs?.organizations ?? []).some((org) => org.itemId === templateOrgId);

  // Load this template's UILM module into i18next so `t(key)` calls
  // in the generate flow resolve to the published UILM strings. Local DB
  // values stay as `defaultValue` fallbacks during the fetch and for any
  // key that hasn't been seeded to UILM yet.
  useEffect(() => {
    if (!template?.slug) return;
    loadTranslations(currentLanguage, slugToCamelCase(template.slug));
  }, [template?.slug, currentLanguage]);

  if (isLoading) {
    return <Loader />;
  }

  if (!template) {
    // notFound();
    return <div>{t('TEMPLATES_GENERATEPAGE_NOTFOUNDTITLE')}</div>;
  }

  if (!canUseTemplate) {
    return (
      <ForbiddenCard
        description={t('FORBIDDEN_TEMPLATE_DESCRIPTION', {
          defaultValue:
            "This template belongs to an organization you're not a member of, so you can't generate a document from it. Ask an admin of that organization to add you.",
        })}
        onBack={() => navigate(-1)}
      />
    );
  }

  // Check if preview token is valid for draft templates
  const isPreviewMode =
    !template.available && preview && template.previewToken && preview === template.previewToken;

  // For templates that aren't available yet, show coming soon page (unless in preview mode)
  if (!template.available && !isPreviewMode) return <ComingSoon template={template} />;

  // Check if template has screens configured
  const hasScreens = template.screens.length > 0;

  // If screens are configured, render the dynamic wizard
  if (hasScreens) {
    // Resolve the template's title + description via UILM once at the page
    // level so every downstream component sees the localized strings
    // without needing its own `useTranslation` call.
    const config = {
      id: template.id,
      slug: template.slug,
      title: tWithFallback(t, template.uilmTitleKey, template.title),
      description: tWithFallback(
        t,
        template.uilmDescriptionKey,
        template.description || '',
      ),
      screens: template.screens,
      systemPrompt: template.systemPrompt,
      systemPromptRole: template.systemPromptRole,
    };

    return (
      <div className="relative">
        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <div className="sticky top-0 z-50 bg-selise-blue/10 border-b border-selise-blue/20 px-4 py-2">
            <div className="container max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-selise-blue" />
                <Badge
                  variant="outline"
                  className="bg-selise-blue/10 text-selise-blue border-selise-blue/30"
                >
                  {t('TEMPLATES_GENERATEPAGE_PREVIEWMODE')}
                </Badge>
                <span className="text-sm text-globe-grey">
                  {t('TEMPLATES_GENERATEPAGE_PREVIEWMODEDESCRIPTION')}
                </span>
              </div>
            </div>
          </div>
        )}
        <DynamicSmartFlow config={config} />
      </div>
    );
  }

  // For available templates without screens configured
  return <NoScreens template={template} />;
};

export default TemplateGeneratePage;
